import filterVerticalIcon from '@/assets/icons/filter-vertical.png';
import filterIcon from '@/assets/icons/project-filter-icon.png';
import { ExpandLess, ExpandMore, Search } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  Menu,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';

export interface FilterState {
  statuses: string[];
  departments: string[];
  fiscalYears: string[];
}

interface ProjectListHeaderProps {
  nameSortOrder: 'asc' | 'desc' | null;
  statusSortOrder: 'asc' | 'desc' | null;
  searchQuery: string;
  filters: FilterState;
  availableDepartments: string[];
  availableFiscalYears: { id: string; label: string }[];
  onNameSort: () => void;
  onStatusSort: () => void;
  onSearchChange: (query: string) => void;
  onFiltersChange: (filters: FilterState) => void;
}

export function ProjectListHeader({
  nameSortOrder: _nameSortOrder,
  statusSortOrder: _statusSortOrder,
  searchQuery,
  filters,
  availableDepartments,
  onNameSort,
  onStatusSort,
  onSearchChange,
  onFiltersChange,
}: ProjectListHeaderProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery('(max-width:1024px)');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );

  const availableStatuses = [
    'Active',
    'Completed',
    'Archived',
    'Pencils Down',
    'Budget Approved',
  ];

  // Custom checkbox icons - filled square with rounded corners
  const CustomCheckboxIcon = () => (
    <Box
      sx={{
        width: 'clamp(14px, 1vw, 16px)',
        height: 'clamp(14px, 1vw, 16px)',
        border: '2px solid #7BBCB3',
        backgroundColor: 'transparent',
        borderRadius: 'clamp(3px, 0.3vw, 4px)',
      }}
    />
  );

  const CustomCheckedIcon = () => (
    <Box
      sx={{
        width: 'clamp(14px, 1vw, 16px)',
        height: 'clamp(14px, 1vw, 16px)',
        backgroundColor: 'info.main',
        border: '2px solid #7BBCB3',
        borderRadius: 'clamp(3px, 0.3vw, 4px)',
      }}
    />
  );

  const handleFilterButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handleStatusToggle = (status: string) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];
    onFiltersChange({ ...filters, statuses: newStatuses });
  };

  const handleDepartmentToggle = (department: string) => {
    const newDepartments = filters.departments.includes(department)
      ? filters.departments.filter((d) => d !== department)
      : [...filters.departments, department];
    onFiltersChange({ ...filters, departments: newDepartments });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(14, 70, 63, 1)',
        borderBottom: '3px solid rgba(0, 61, 54, 1)',
        px: 'clamp(12px, 1.04vw, 20px)',
        height: 'clamp(22px, 2.3vw, 38px)',
        minHeight: 'clamp(22px, 2.3vw, 38px)',
        mb: 0,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexShrink: 0,
        minWidth: 0,
        overflow: 'visible',
        width: '100%',
      }}
    >
      {/* Gap before buttons - matches project cards */}
      <Box sx={{ width: 'clamp(7px, 0.42vw, 8px)', flexShrink: 0 }} />

      {/* Buttons column spacer - matches project cards */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(1px, 0.16vw, 3px)',
          width: 'clamp(36px, 2.24vw, 43px)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 'clamp(14px, 1.46vw, 28px)' }} />
        <Box sx={{ width: 'clamp(14px, 1.46vw, 28px)' }} />
      </Box>

      {/* Gap after buttons before title - matches project cards */}
      <Box sx={{ width: 'clamp(48px, 3.33vw, 64px)', flexShrink: 0 }} />

      {/* Project Name column - matches project cards */}
      <Box
        sx={{
          width: isSmallScreen ? '20%' : '15%',
          flexShrink: 0,
          minWidth: 'clamp(100px, 6.25vw, 120px)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Button
          disableRipple
          onClick={onNameSort}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(6px, 0.52vw, 10px)',
            textTransform: 'none',
            color: '#FFFFFF',
            backgroundColor: 'transparent !important',
            boxShadow: 'none !important',
            p: 0,
            m: 0,
            minWidth: 0,
            justifyContent: 'flex-start',
            '&:hover': {
              backgroundColor: 'transparent !important',
              boxShadow: 'none !important',
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(10px, 0.73vw, 14px)',
              color: '#FFFFFF',
              lineHeight: 1,
            }}
          >
            Project Name
          </Typography>
          <Box
            component='img'
            src={filterIcon}
            alt='Filter'
            sx={{
              width: 'clamp(6px, 0.42vw, 8px)',
              height: 'clamp(9px, 0.625vw, 12px)',
              imageRendering: 'crisp-edges',
              display: 'block',
            }}
          />
        </Button>
      </Box>

      {/* Gap between name and description - hide on small screens */}
      {!isSmallScreen && (
        <Box sx={{ width: 'clamp(3px, 1.82vw, 35px)', flexShrink: 0 }} />
      )}

      {/* Description column - hide on small screens */}
      {!isSmallScreen && (
        <Box
          sx={{
            width: {
              xs: '7%',
              sm: '8%',
              md: '9%',
              lg: '10%',
              xl: '13%',
              '@media (min-width: 1920px)': {
                width: '16%',
              },
            },
            flexShrink: 0,
            minWidth: {
              xs: 'clamp(25px, 4vw, 75px)',
              md: 'clamp(30px, 4.5vw, 90px)',
              lg: 'clamp(30px, 4.8vw, 100px)',
              xl: 'clamp(35px, 6vw, 130px)',
              '@media (min-width: 1920px)': {
                minWidth: 'clamp(50px, 7vw, 150px)',
              },
            },
          }}
        />
      )}

      {/* Gap between description and status */}
      <Box
        sx={{
          width: isSmallScreen
            ? 'clamp(10px, 4vw, 60px)'
            : 'clamp(2px, 2.5vw, 48px)',
          flexShrink: 0,
        }}
      />

      {/* Status column - matches project cards */}
      <Box
        sx={{
          display: 'flex',
          gap: 'clamp(6px, 0.52vw, 10px)',
          width: '18%',
          flexShrink: 0,
          minWidth: 'clamp(125px, 7.81vw, 150px)',
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Button
          disableRipple
          onClick={onStatusSort}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(6px, 0.52vw, 10px)',
            textTransform: 'none',
            color: '#FFFFFF',
            backgroundColor: 'transparent !important',
            boxShadow: 'none !important',
            p: 0,
            m: 0,
            minWidth: 0,
            '&:hover': {
              backgroundColor: 'transparent !important',
              boxShadow: 'none !important',
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(10px, 0.73vw, 14px)',
              color: '#FFFFFF',
              lineHeight: 1,
            }}
          >
            Status
          </Typography>
          <Box
            component='img'
            src={filterIcon}
            alt='Filter'
            sx={{
              width: 'clamp(6px, 0.42vw, 8px)',
              height: 'clamp(9px, 0.625vw, 12px)',
              imageRendering: 'crisp-edges',
              display: 'block',
            }}
          />
        </Button>
      </Box>

       {/* Gap between status and subprojects */}
       <Box
         sx={{
           width: isSmallScreen
           ? 'clamp(20px, 2.5vw, 40px)'
           : 'clamp(8px, 1vw, 20px)',
           flexShrink: 0,
         }}
       />

       {/* Subprojects column */}
       <Box
         sx={{
           px: 'clamp(0px, 1.2vw, 0px)',
           py: 'clamp(8px, 0.6vh, 10px)',
           display: 'flex',
           width: '18%',
           alignItems: 'left',
           justifyContent: 'space-between',
         }}

        >
	<Typography
	    sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(10px, 0.73vw, 14px)',
              color: '#FFFFFF',
              lineHeight: 1,
              margin: 0,
            }}
        >
          Subproject Progress
        </Typography>
        </Box>

      {/* Flexible spacer to push search to the right */}
      <Box sx={{ flex: 1, minWidth: 'clamp(20px, 2vw, 40px)' }} />

      {/* Search bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          marginTop: '-2px',
          marginRight: 'clamp(14px, 1.5vw, 24px)',
        }}
      >
        <TextField
          placeholder='Search Projects...'
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          size='small'
          sx={{
            width: 'clamp(160px, 13vw, 210px) !important',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(213, 228, 213, 1)',
              color: 'rgba(14, 70, 63, 1)',
              fontSize: 'clamp(10px, 0.8vw, 15px)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              borderRadius: '0 !important',
              height: 'clamp(20px, 1.6vw, 30px) !important',
              paddingRight: 'clamp(2px, 0.3vw, 6px)',
              '& fieldset': {
                borderColor: 'rgba(14, 70, 63, 1)',
                borderWidth: '1px',
                borderRadius: 0,
              },
              '&:hover fieldset': {
                borderColor: 'rgba(14, 70, 63, 1)',
                borderWidth: '1px',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'rgba(14, 70, 63, 1)',
                borderWidth: '1px',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: 'clamp(2px, 0.35vw, 6px) clamp(6px, 0.8vw, 14px)',
              textAlign: 'left',
              '&::placeholder': {
                color: 'rgba(14, 70, 63, 1)',
                opacity: 1,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position='end'
                sx={{ marginLeft: '0px', marginRight: '4px' }}
              >
                <Search
                  sx={{
                    color: 'rgba(0, 61, 54, 1)',
                    fontSize: 'clamp(16px, 1.3vw, 25px)',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filter Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          marginTop: '-2px',
        }}
      >
        <IconButton
          onClick={handleFilterButtonClick}
          sx={{
            width: 'clamp(14px, 1.2vw, 24px)',
            height: 'clamp(14px, 1.2vw, 24px)',
            padding: 0,
            alignSelf: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <img
            src={filterVerticalIcon}
            alt='Filter'
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)',
            }}
          />
        </IconButton>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={handleFilterMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(14, 70, 63, 1)',
            borderRadius: 0,
            minWidth: 'clamp(250px, 20vw, 350px)',
            maxHeight: '600px',
            overflowY: 'auto',
            border: 'none',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 'clamp(14px, 1.2vw, 20px)',
            py: 'clamp(10px, 0.8vh, 14px)',
            borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 'clamp(12px, 1vw, 16px)',
              fontWeight: 700,
              color: '#FFFFFF',
            }}
          >
            Filter by
          </Typography>
        </Box>

        {/* Year Filter Section was removed due to confusion in the search/filter results*/}

        {/* Status Filter Section */}
        <Box>
          <Box
            onClick={() => toggleSection('status')}
            sx={{
              px: 'clamp(14px, 1.2vw, 20px)',
              py: 'clamp(8px, 0.6vh, 10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.info.main, 0.1),
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(11px, 0.85vw, 13px)',
                fontWeight: 600,
                color: '#FFFFFF',
              }}
            >
              Status
            </Typography>
            {expandedSections.has('status') ? (
              <ExpandLess
                sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)', color: '#FFFFFF' }}
              />
            ) : (
              <ExpandMore
                sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)', color: '#FFFFFF' }}
              />
            )}
          </Box>
          <Collapse in={expandedSections.has('status')}>
            <Box
              sx={{
                px: 'clamp(14px, 1.2vw, 20px)',
                py: 'clamp(10px, 0.8vh, 14px)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'clamp(4px, 0.3vh, 6px)',
                }}
              >
                {availableStatuses.map((status) => (
                  <Box
                    key={status}
                    onClick={() => handleStatusToggle(status)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    <Checkbox
                      checked={filters.statuses.includes(status)}
                      icon={<CustomCheckboxIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      size='small'
                      sx={{
                        padding: 'clamp(2px, 0.2vw, 4px)',
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(9px, 0.7vw, 11px)',
                        fontWeight: 400,
                        color: '#FFFFFF',
                      }}
                    >
                      {status}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* Department Filter Section */}
        <Box>
          <Box
            onClick={() => toggleSection('department')}
            sx={{
              px: 'clamp(14px, 1.2vw, 20px)',
              py: 'clamp(8px, 0.6vh, 10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: alpha(theme.palette.info.main, 0.1),
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(11px, 0.85vw, 13px)',
                fontWeight: 600,
                color: '#FFFFFF',
              }}
            >
              Department
            </Typography>
            {expandedSections.has('department') ? (
              <ExpandLess
                sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)', color: '#FFFFFF' }}
              />
            ) : (
              <ExpandMore
                sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)', color: '#FFFFFF' }}
              />
            )}
          </Box>
          <Collapse in={expandedSections.has('department')}>
            <Box
              sx={{
                px: 'clamp(14px, 1.2vw, 20px)',
                py: 'clamp(10px, 0.8vh, 14px)',
              }}
            >
              {availableDepartments.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'clamp(4px, 0.3vh, 6px)',
                  }}
                >
                  {availableDepartments.map((department) => (
                    <Box
                      key={department}
                      onClick={() => handleDepartmentToggle(department)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Checkbox
                        checked={filters.departments.includes(department)}
                        icon={<CustomCheckboxIcon />}
                        checkedIcon={<CustomCheckedIcon />}
                        size='small'
                        sx={{
                          padding: 'clamp(2px, 0.2vw, 4px)',
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 'clamp(9px, 0.7vw, 11px)',
                          fontWeight: 400,
                          color: '#FFFFFF',
                        }}
                      >
                        {department}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(9px, 0.7vw, 11px)',
                    fontWeight: 400,
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontStyle: 'italic',
                  }}
                >
                  No departments assigned to projects
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      </Menu>
    </Box>
  );
}
